<template>
	<div :class="['battery-container', { single: isSingle }]">
		<div class="device" v-for="(device, index) in slots" :key="index">
			<img class="track" src="./images/ring-track.svg" alt="" />
			<svg class="progress" viewBox="0 0 152 152">
				<circle
					v-if="device && device.level > 0"
					cx="76" cy="76" :r="radius"
					:stroke="levelColor(device)"
					:stroke-dasharray="dash(device.level)"
					:transform="rotation"
				/>
			</svg>
			<template v-if="device">
				<img class="icon" :src="icons[device.type] || icons.headset" alt="" />
				<p class="level">
					<svg v-if="device.charging" class="charging" viewBox="0 0 24 24"><path d="M13 2 4 14h7l-1 8 9-12h-7z" /></svg>
					{{ Math.round(device.level) }}
				</p>
			</template>
		</div>
	</div>
</template>

<script>
	import { isInUnity, onUnityMessage, postToUnity } from '../utils/unityBridge';
	import headsetIcon from './images/headset.svg';
	import leftControllerIcon from './images/left-controller.svg';
	import rightControllerIcon from './images/right-controller.svg';

	const SLOT_COUNT = 4;
	// The ring is open at the bottom, where the percentage is shown (measured from the Figma track)
	const ARC_DEGREES = 287;
	const RADIUS = 68;

	// Shown when the page is opened outside of the Unity app, so the UI can be previewed
	const DEMO_DEVICES = [
		{ type: 'headset', level: 45, charging: false },
		{ type: 'rightController', level: 83, charging: false },
		{ type: 'leftController', level: 96, charging: false },
	];

	// ?demo=1 previews the single-device layout outside of Unity.
	function demoDevices() {
		const count = Number(new URLSearchParams(window.location.search).get('demo'));
		return count > 0 ? DEMO_DEVICES.slice(0, count) : DEMO_DEVICES;
	}

	export default {
		name: 'BatteryComponent',
		data() {
			return {
				radius: RADIUS,
				rotation: `rotate(${90 + (360 - ARC_DEGREES) / 2} 76 76)`,
				devices: isInUnity() ? [] : demoDevices(),
				unsubscribe: null,
				icons: {
					headset: headsetIcon,
					leftController: leftControllerIcon,
					rightController: rightControllerIcon,
				},
			}
		},
		computed: {
			// On OpenXR Unity can only read the headset battery, so usually there is one device:
			// show it as one large ring instead of a grid with three empty slots.
			isSingle() {
				return this.devices.length === 1;
			},
			slots() {
				if (this.isSingle) {
					return this.devices;
				}
				return Array.from({ length: SLOT_COUNT }, (_, index) => this.devices[index] || null);
			},
		},
		mounted() {
			this.unsubscribe = onUnityMessage('battery.update', (devices) => {
				if (Array.isArray(devices)) {
					this.devices = devices.filter(device => device && typeof device.level === 'number');
				}
			});
			postToUnity('battery.requested');
		},
		unmounted() {
			if (this.unsubscribe) {
				this.unsubscribe();
			}
		},
		methods: {
			dash(level) {
				const circumference = 2 * Math.PI * RADIUS;
				const length = circumference * (ARC_DEGREES / 360) * Math.min(100, Math.max(0, level)) / 100;
				return `${length} ${circumference}`;
			},
			levelColor(device) {
				if (device.charging || device.level > 50) {
					return '#32D74B';
				}
				if (device.level > 20) {
					return '#FFA500';
				}
				// Not in the design, low battery needs to stand out
				return '#FF453A';
			},
		},
	}
</script>

<style lang="scss">
	html, body {margin: 0; height: 100%; overflow: hidden}
</style>

<style scoped lang="scss">
	.battery-container {
		width: 400px;
		height: 400px;
		background-color: #323437;
		padding: 32px;
		box-sizing: border-box;
		display: grid;
		grid-template-columns: repeat(2, 152px);
		grid-template-rows: repeat(2, 152px);
		gap: 32px;

		.device {
			position: relative;
			width: 152px;
			height: 152px;

			.track {
				position: absolute;
				top: 0;
				left: 0;
				width: 152px;
				height: 138.807px;
			}

			.progress {
				position: absolute;
				inset: 0;

				circle {
					fill: none;
					stroke-width: 8;
					stroke-linecap: round;
					transition: stroke-dasharray 600ms ease-out, stroke 300ms ease-out;
				}
			}

			.icon {
				position: absolute;
				top: 48px;
				left: 48px;
				width: 56px;
				height: 56px;
			}

			.level {
				position: absolute;
				left: 0;
				right: 0;
				top: 140px;
				transform: translateY(-50%);
				margin: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 2px;
				color: #FFFFFF;
				font-family: Inter, sans-serif;
				font-size: 28px;
				font-weight: 600;
				line-height: 1;
				letter-spacing: -0.56px;
				text-shadow: 0px 2px 14px #00000029;

				.charging {
					width: 18px;
					height: 18px;
					fill: #FFFFFF;
				}
			}
		}

		&.single {
			grid-template-columns: 152px;
			grid-template-rows: 152px;
			place-content: center;

			.device {
				transform: scale(2);
			}
		}
	}
</style>
